/**
 * Markdown Service
 * Implements basic Markdown to HTML conversion
 * Supports standard Markdown features without syntax highlighting
 */

import { EncodingOperation } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';
import { CommonValidator } from '../validation/commonValidation';

export class MarkdownService {
  /**
   * Convert Markdown to HTML
   */
  static async toHtml(markdown: string): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(markdown,
        CommonValidator.getValidationRules('markdown-preview' as any)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, 'markdown-preview' as any, validation.suggestion);
      }

      // Convert to HTML
      const html = this.convertToHtml(markdown);

      return {
        id: this.generateOperationId(),
        toolType: 'markdown-preview' as any,
        operationType: 'convert',
        inputData: markdown,
        outputData: html,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          markdownLength: markdown.length,
          htmlLength: html.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to convert Markdown to HTML',
        'markdown-preview' as any,
        'Please check your Markdown syntax and try again'
      );
    }
  }

  /**
   * Core Markdown to HTML conversion
   */
  private static convertToHtml(markdown: string): string {
    let html = markdown;

    // Headers (h1-h6)
    html = html.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
    html = html.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
    html = html.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

    // Bold (**text** or __text__)
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Italic (*text* or _text_)
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.+?)_/g, '<em>$1</em>');

    // Code blocks (```code```)
    html = html.replace(/```([^`]+)```/g, '<pre><code>$1</code></pre>');

    // Inline code (`code`)
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    // Images ![alt](url) - MUST come before links
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');

    // Links [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Horizontal rule (--- or ***)
    html = html.replace(/^---$/gm, '<hr />');
    html = html.replace(/^\*\*\*$/gm, '<hr />');

    // Blockquotes (> text)
    html = html.replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>');

    // Unordered lists (- item or * item)
    html = html.replace(/^[\-\*]\s+(.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

    // Ordered lists (1. item)
    html = html.replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>');

    // Paragraphs (double line break)
    const paragraphs = html.split(/\n\n+/);
    html = paragraphs.map(p => {
      p = p.trim();
      if (p && !p.match(/^<(h[1-6]|ul|ol|pre|blockquote|hr)/)) {
        return `<p>${p}</p>`;
      }
      return p;
    }).join('\n');

    // Line breaks
    html = html.replace(/\n/g, '<br />');

    return html;
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `markdown-preview-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
